import { THERAPEUTIC_FOCUS, TREATMENT_METHODS, } from '../user/UserApi.js';
export const de = {
    locale: 'de',
    common: {
        paging: {
            prev: 'vorherige Seite',
            next: 'nächste Seite',
            noSearchResults: 'Leider konnten wir keine passenden Ergebnisse zu Ihrer Suche finden.',
        },
        button: {
            search: 'Suchen',
            next: 'Weiter',
        },
        error: {
            network: 'Es ist ein Netzwerkfehler aufgetreten. Bitte versuchen Sie es später erneut.',
            isRequired: 'Dies ist ein Pflichtfeld.',
        },
        term: {
            Yes: 'Ja',
            No: 'Nein',
        },
        validation(error, _context) {
            if (error.keyword === 'type') {
                if (error.params.type === 'string') {
                    return 'Dies ist ein Pflichtfeld.';
                }
            }
            if (error.keyword === 'enum') {
                if (error.params.allowedValues) {
                    // the value is not one of the allowed values,
                    // but the only case is if the value is missing
                    return 'Dies ist ein Pflichtfeld.';
                }
            }
            if (error.keyword === 'format') {
                if (error.params.format === 'idn-email') {
                    return 'Es muss eine gültige E-Mail-Adresse eingegeben werden.';
                }
            }
            if (error.keyword === 'pattern') {
                if (error.params.pattern === '^[0-9]+$') {
                    return 'Es dürfen nur Zahlen eingegeben werden.';
                }
            }
            return error.message ?? 'Ungültiger Wert';
        },
        date(date) {
            return date.toFormat('dd.LL.y');
        },
        dateTime(date) {
            return date.toFormat('dd.LL.y HH:mm');
        },
    },
    user: {
        PatientNumber: 'Patientennummer',
        Salutation: 'Anrede',
        Title: 'Titel',
        FirstName: 'Vorname',
        LastName: 'Nachname',
        EMail: 'E-Mail Adresse',
        PhoneCountry: 'Landesvorwahl',
        PhoneMobile: 'Mobilnummer',
        InsuranceType: 'Versicherungsart',
        insuranceTypeLabel(type) {
            return {
                legally: 'Gesetzlich versichert',
                privately: 'Privat versichert / Selbstzahler',
            }[type];
        },
        Birthday: 'Geburtstag',
        CareOf: 'c/o',
        Street: 'Straße, Hausnummer',
        PostalCode: 'Postleitzahl',
        City: 'Stadt',
        Country: 'Land (Postadresse)',
        InvoicePostalOnly: 'Rechnungen ausschließlich per Post gewünscht',
        InvoiceAddress: 'Abweichende Rechnungsadresse',
        InternalNote: 'Notizen',
        InvoiceRemark: 'Bemerkung auf der Rechnung',
        name(user) {
            return [user.title, user.firstName, user.lastName].filter(Boolean).join(' ');
        },
        JobTitle: 'Berufsgruppe',
        jobTitles: {
            Frau: {
                none: '',
                psychologicalPsychotherapist: 'Psychologische Psychotherapeutin',
                psychologicalPsychotherapistChildren: 'Psychologische Psychotherapeutin für Kinder und Jugendliche',
                specialistPsychiatryPsychotherapy: 'Fachärztin für Psychiatrie und Psychotherapie',
                specialistPsychiatryPsychotherapyChildren: 'Fachärztin für Kinder- und Jugendlichenpsychiatrie und -psychotherapie',
                specialistPsychosomatics: 'Fachärztin für Psychosomatische Medizin und Psychotherapie',
                physicianAdditionalQualificationPsychotherapy: 'Ärztin mit Zusatzbezeichnung Psychotherapie',
                nonMedicalPractitionerPsychotherapy: 'Heilpraktikerin für Psychotherapie',
                socialPedagogue: 'Sozialpädagogin',
                coachOrConsultant: 'Coach / Beraterin',
            },
            Other: {
                none: '',
                psychologicalPsychotherapist: 'Psychologischer Psychotherapeut',
                psychologicalPsychotherapistChildren: 'Psychologischer Psychotherapeut für Kinder und Jugendliche',
                specialistPsychiatryPsychotherapy: 'Facharzt für Psychiatrie und Psychotherapie',
                specialistPsychiatryPsychotherapyChildren: 'Facharzt für Kinder- und Jugendlichenpsychiatrie und -psychotherapie',
                specialistPsychosomatics: 'Facharzt für Psychosomatische Medizin und Psychotherapie',
                physicianAdditionalQualificationPsychotherapy: 'Arzt mit Zusatzbezeichnung Psychotherapie',
                nonMedicalPractitionerPsychotherapy: 'Heilpraktiker für Psychotherapie',
                socialPedagogue: 'Sozialpädagoge',
                coachOrConsultant: 'Coach / Berater',
            },
        },
        jobTitle(data) {
            return data.jobTitleFreetext
                ?? this.jobTitles[data.salutation === 'Frau' ? 'Frau' : 'Other'][data.jobTitleType ?? 'none'];
        },
        TherapeuticFocus: 'Therapieschwerpunkte',
        TherapeuticFocusShort: 'Schwerpunkte',
        therapeuticFocusLabel(focus) {
            return THERAPEUTIC_FOCUS.find(item => item[0] === focus)?.[1][0] ?? focus;
        },
        TreatmentMethods: 'Verfahren',
        treatmentMethodLabel(method) {
            return TREATMENT_METHODS.find(item => item[0] === method)?.[1][0] ?? method;
        },
        CoverageType: 'Abrechnung',
        coverageTypeLabel(type) {
            return {
                'Private Health Insurance': 'Private Krankenkassen',
                'Statutory Health Insurance': 'Gesetzliche Krankenkassen',
                'Self-Payment': 'Selbstzahlende',
                'Reimbursement Procedure': 'Kostenerstattungsverfahren',
            }[type];
        },
        Language: 'Sprache',
        Profile: 'Profil',
        Gender: 'Gender',
        genderLabel(gender) {
            const labels = { male: 'Männlich', female: 'Weiblich', diverse: 'Divers' };
            return labels[gender];
        },
        searchTherapist: {
            metaTitle(params) {
                return `Psychotherapeuten in ${params.city}: Termin online buchen`;
            },
            metaDescription(params) {
                return `In ${params.city} finden Sie ${params.count === 0 ? '' : params.count} Psychotherapeuten, Psychologen, Ärzte und Heilpraktiker für Psychotherapie. Einfache Online-Terminbuchung.`;
            },
            resultCount(count) {
                const rule = new Intl.PluralRules('de-DE').select(count);
                if (rule === 'one') {
                    return `${count} Ergebnis gefunden`;
                }
                return `${count} Ergebnisse gefunden`;
            },
            distanceKm(km) {
                return `${Math.round(km)} km`;
            },
            ToProfile: 'Zum Profil',
            YourProfile: 'Ihr Profil?\nAngaben anpassen',
            noAvailableAppointments(type) {
                if (type === 'external') {
                    return 'Keine Online-Terminbuchung über Tymia verfügbar';
                }
                return 'Aktuell keine freien Termine';
            },
            AvailableRemoteAppointment: 'Freier Video-Termin',
            filter: {
                reset: 'Filter zurücksetzen',
                showResult: 'Ergebnisse anzeigen',
                address: {
                    placeholder: 'z. B. München oder 80337',
                },
                location: {
                    label: 'Vor Ort/Video',
                    options: {
                        onSite: 'Vor Ort',
                        remote: 'Video',
                    },
                },
                sort: {
                    label: 'Sortieren nach',
                    options: {
                        distance: 'Entfernung',
                        availability: 'Verfügbarkeit',
                    },
                },
            },
        },
        export: {
            PatientRecord: 'Patientenakte',
            birthdayLabel(birthday) {
                return `geb. ${birthday}`;
            },
            Diagnoses: 'Diagnosen',
            MainDiagnosis: 'Haupt-Diagnose',
            File: 'Datei',
            MedicalHistory: 'Verlaufsdokumentation',
            MedicalHistoryArchive: 'Archiv Verlaufsdokumentation\n(Bearbeitete oder gelöschte Einträge)',
            Appointments: 'Termine',
            Invoices: 'Rechnungsübersicht',
            InvoiceHeaderLine: 'Rechnungsnumer; Rechnungsdatum; Name, Vorname; Rechnungsbetrag; Status',
            taxedLabel: 'inkl. MwSt.',
        },
    },
    client: {
        invoice: {
            download: {
                metaTitle: 'Rechnung herunterladen',
                title: {
                    error: 'Fehler',
                    enterTan: 'Identität bestätigen',
                    download: 'Rechnung herunterladen',
                },
                message: {
                    invalidToken: 'Der Download-Link ist ungültig oder abgelaufen.',
                    notMobile: 'Die hinterlegte Nummer ist keine gültige Mobilnummer.',
                    enterTan(phone) {
                        return `Bitte geben Sie die TAN ein, die wir per SMS an ${phone} geschickt haben.`;
                    },
                    rateLimited: 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
                    tooManyTansRequested: 'Sie haben bereits mehrere TANs angefordert. Keine weitere TAN möglich.',
                    invalidTan: 'Die eingegebene TAN ist ungültig.',
                },
                input: {
                    tan: {
                        label: 'TAN',
                    },
                    submit: {
                        label: 'Rechnung herunterladen',
                    },
                },
            },
        },
        medicalHistory: {
            stateLabel(state) {
                return {
                    active: 'Aktiv',
                    changed: 'Geändert',
                    deleted: 'Gelöscht',
                }[state];
            },
        },
    },
    appointment: {
        bookingWidget: {
            SelectInsuranceTypeLabel: 'Bitte wählen Sie Ihre Versicherungsart',
            SelectInsuranceTypeSelectEmpty: 'Bitte zuerst Versicherungsart wählen',
            SelectInsuranceTypeSelectSelected: 'Versicherungsart',
            insuranceTypeOption(type) {
                return de.user.insuranceTypeLabel(type);
            },
            meetingTypeOption(meetingType) {
                const location = {
                    onSite: 'vor Ort',
                    remote: 'online',
                    both: 'vor Ort und Online',
                }[meetingType.location];
                return `${meetingType.name} ${location} (${meetingType.duration} Minuten)`;
            },
            SelectMeetingTypeLabel: 'Terminart wählen',
            SelectMeetingTypeSelectEmpty: 'Bitte zuerst Terminart wählen',
            SelectMeetingTypeSelectSelected: 'Terminart',
            SelectAppointment: 'Termin auswählen',
            NoAppointments: 'Aktuell keine freien Termine',
            PersonalData: 'Angaben zu Ihrer Person',
            SelectLocationLabel: 'Ort des Termins wählen',
            PrivacyConsent: 'Datenschutzerklärung',
            PrivacyConsentText: 'Meine Daten werden im Auftrag der Praxis durch die Tymia GmbH (Praxissoftware) zur Terminbuchung verarbeitet.',
            PrivacyConsentLabel: 'Ich bin damit einverstanden, dass ich Terminbestätigungen per E-Mail erhalte.',
            BookAppointment: 'Termin buchen',
            BookingSuccessHeader: 'Termin wurde erfolgreich gebucht',
            BookingSuccessMessage: 'Bitte überprüfen Sie Ihr E-Mail Postfach. Alle weiteren Informationen werden Ihnen an die angegebene E-Mail geschickt.',
            bookingSuccessDetails(appointment) {
                return `Terminbeginn: ${de.common.dateTime(appointment.appointmentStart)}`;
            },
        },
    },
    invoice: {
        stateLabel(stateDescriptor) {
            switch (stateDescriptor.key) {
                case 'CANCELED':
                    return 'Storniert';
                case 'CANCELED_SENT':
                    return 'Storniert (versandt)';
                case 'PAID':
                    return 'Bezahlt';
                case 'PAID_SENT':
                    return 'Bezahlt (versandt)';
                case 'CREATED':
                    return 'Erstellt';
                case 'SENT':
                    return 'Versandt';
                case 'REMINDER_CREATED':
                    return `${stateDescriptor.number}. Zahlungserinnerung erstellt`;
                case 'REMINDER_SENT':
                    return `${stateDescriptor.number}. Zahlungserinnerung versandt`;
                case 'FINAL_NOTICE_CREATED':
                    return 'Finale Mahnung erstellt';
                case 'FINAL_NOTICE_SENT':
                    return 'Finale Mahnung versandt';
                case 'ERROR':
                    return 'Fehler';
            }
        },
    },
};
//# sourceMappingURL=de.js.map